<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container fluid class="expiring-root-container">
        <!-- TRADIES -->
        <b-card
          class="expiring-group-card expiring-group-card--tradie mt-4 mb-4"
          body-class="px-0 py-3"
        >
          <template #header>
            <strong>Tradies</strong>
          </template>

          <b-container fluid>
            <b-row align-h="between" align-v="center">
              <b-col
                cols="12"
                sm="12"
                md="5"
                lg="4"
                class="expiring-search-col"
              >
                <b-form-input
                  v-model="tradieSearch"
                  placeholder="Owner, project or file name"
                  class="expiring-search-input"
                />
              </b-col>
            </b-row>

            <!-- Mobile: stacked expiring-file rows (hidden on md+) -->
            <div class="d-md-none mobile-file-list">
              <div
                v-for="item in tradieMobilePage"
                :key="item.id"
                class="mobile-file-item"
                :class="getRowClass(item)"
                @click="viewFile(item)"
              >
                <div class="mobile-file__body">
                  <span
                    class="mobile-file__name"
                    :class="{ 'text-muted': isTradieTokenLocked(item) }"
                  >
                    {{ fileOwnerName(item) }}
                  </span>
                  <div
                    v-if="canShowTradieField(item)"
                    class="mobile-file__meta"
                  >
                    <span v-if="item.file_name">{{ item.file_name }}</span>
                    <span v-if="item.expiry_date">
                      · Exp {{ item.expiry_date }}</span
                    >
                    <span v-if="item.days_left != null">
                      · {{ item.days_left }} days
                    </span>
                    <span v-if="item.last_renewal_requested_at">
                      · Last requested {{ item.last_renewal_requested_at }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="mobile-file__meta mobile-file__meta--lock d-flex align-items-center"
                  >
                    <img
                      src="@/assets/images/lock.png"
                      alt="Locked"
                      class="mobile-file__lock-icon mr-1"
                    />
                    {{ tradieLockMessage() }}
                  </div>
                </div>
                <div
                  v-if="canRequestRenewal(item)"
                  class="mobile-file__action"
                  @click.stop
                >
                  <b-button
                    variant="primary"
                    class="mobile-file__renew-btn d-inline-flex align-items-center justify-content-center"
                    :disabled="isRequestingRenewal(item.id)"
                    :title="renewalButtonTitle(item)"
                    @click="requestRenewal(item)"
                  >
                    <b-spinner v-if="isRequestingRenewal(item.id)" small />
                    <b-icon v-else icon="arrow-repeat" />
                  </b-button>
                </div>
              </div>
              <div
                v-if="!tradieMobilePage.length"
                class="text-center py-4 text-muted"
              >
                No expiring files.
              </div>
            </div>

            <!-- Expiring files table (desktop) -->
            <div class="table-wrapper d-none d-md-block">
              <b-table
                id="tradie-files-table"
                class="expiring-table-spacing tradies-table"
                responsive
                hover
                show-empty
                :fields="fields"
                :items="tradieFiles"
                :per-page="perPage"
                :current-page="tradieCurrentPage"
                :tbody-tr-class="getRowClass"
                @row-clicked="viewFile"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <digger-loader />
                  </div>
                </template>

                <template #cell(file_owner_name)="data">
                  <div class="cell-content-wrapper">
                    <span
                      :class="{
                        'text-muted': isTradieTokenLocked(data.item),
                      }"
                    >
                      {{ fileOwnerName(data.item) }}
                    </span>
                    <div
                      v-if="isTradieTokenLocked(data.item)"
                      class="lock-overlay"
                      :ref="`lock-over-lay-${data.item.id}`"
                    >
                      <img
                        src="@/assets/images/lock.png"
                        alt="Locked"
                        class="lock-icon mr-2"
                      />
                      <span>{{ tradieLockMessage() }}</span>
                    </div>
                  </div>
                </template>

                <template #cell(project_names)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.project_names
                  }}</span>
                </template>

                <template #cell(file_name)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.file_name
                  }}</span>
                </template>

                <template #cell(issued_date)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.issued_date
                  }}</span>
                </template>

                <template #cell(expiry_date)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.expiry_date
                  }}</span>
                </template>

                <template #cell(days_left)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.days_left
                  }}</span>
                </template>

                <template #cell(last_renewal_requested_at)="data">
                  <span v-if="canShowTradieField(data.item)">
                    <template v-if="data.item.last_renewal_requested_at">
                      {{ data.item.last_renewal_requested_at }}
                      <div
                        v-if="data.item.last_renewal_requested_by"
                        class="last-requested-by"
                      >
                        by {{ data.item.last_renewal_requested_by }}
                      </div>
                    </template>
                    <span v-else>-</span>
                  </span>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    pill
                    variant="primary"
                    class="renew-btn d-inline-flex align-items-center justify-content-center"
                    v-if="canRequestRenewal(data.item)"
                    :disabled="isRequestingRenewal(data.item.id)"
                    :title="renewalButtonTitle(data.item)"
                    @click.stop="requestRenewal(data.item)"
                  >
                    <template v-if="isRequestingRenewal(data.item.id)">
                      <b-spinner small label="Loading" />
                      <span class="d-none d-md-inline ml-2">Sending...</span>
                    </template>

                    <template v-else>
                      <b-icon icon="arrow-repeat" class="d-md-none" />
                      <span class="d-none d-md-inline">Request Renewal</span>
                    </template>
                  </b-button>
                </template>
              </b-table>
            </div>

            <b-pagination
              v-if="tradieFiles.length > perPage"
              v-model="tradieCurrentPage"
              :total-rows="tradieFiles.length"
              :per-page="perPage"
              :limit="5"
              aria-controls="tradie-files-table"
              align="center"
              class="tradies-pagination"
            />

            <p v-if="!isLoading" align="center" class="tradie-count">
              Showing {{ tradieShowingCount }} out of total of
              {{ tradieFiles.length }} Expiring Files
            </p>
          </b-container>
        </b-card>

        <!-- COMPANY FILES -->
        <b-card
          v-if="!hideCompanyFiles"
          class="expiring-group-card expiring-group-card--company mb-4"
          body-class="px-0 py-3"
        >
          <template #header>
            <strong>Company Files</strong>
          </template>

          <b-container fluid>
            <b-row align-h="between" align-v="center">
              <b-col
                cols="12"
                sm="12"
                md="5"
                lg="4"
                class="expiring-search-col"
              >
                <b-form-input
                  v-model="companySearch"
                  placeholder="Owner, project or file name"
                  class="expiring-search-input"
                />
              </b-col>
            </b-row>

            <!-- Mobile: stacked expiring-file rows (hidden on md+) -->
            <div class="d-md-none mobile-file-list">
              <div
                v-for="item in companyMobilePage"
                :key="item.id"
                class="mobile-file-item"
                :class="getRowClass(item)"
                @click="viewFile(item)"
              >
                <div class="mobile-file__body">
                  <span
                    class="mobile-file__name"
                    :class="{ 'text-muted': isTradieTokenLocked(item) }"
                  >
                    {{ fileOwnerName(item) }}
                  </span>
                  <div
                    v-if="canShowTradieField(item)"
                    class="mobile-file__meta"
                  >
                    <span>{{ companyFileTypeLabel(item) }}</span>
                    <span v-if="item.file_name"> · {{ item.file_name }}</span>
                    <span v-if="item.expiry_date">
                      · Exp {{ item.expiry_date }}</span
                    >
                    <span v-if="item.days_left != null">
                      · {{ item.days_left }} days
                    </span>
                    <span v-if="item.last_renewal_requested_at">
                      · Last requested {{ item.last_renewal_requested_at }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="mobile-file__meta mobile-file__meta--lock d-flex align-items-center"
                  >
                    <img
                      src="@/assets/images/lock.png"
                      alt="Locked"
                      class="mobile-file__lock-icon mr-1"
                    />
                    {{ tradieLockMessage() }}
                  </div>
                </div>
                <div
                  v-if="canRequestRenewal(item)"
                  class="mobile-file__action"
                  @click.stop
                >
                  <b-button
                    variant="primary"
                    class="mobile-file__renew-btn d-inline-flex align-items-center justify-content-center"
                    :disabled="isRequestingRenewal(item.id)"
                    :title="renewalButtonTitle(item)"
                    @click="requestRenewal(item)"
                  >
                    <b-spinner v-if="isRequestingRenewal(item.id)" small />
                    <b-icon v-else icon="arrow-repeat" />
                  </b-button>
                </div>
              </div>
              <div
                v-if="!companyMobilePage.length"
                class="text-center py-4 text-muted"
              >
                No expiring files.
              </div>
            </div>

            <!-- Expiring files table (desktop) -->
            <div class="table-wrapper d-none d-md-block">
              <b-table
                id="company-files-table"
                class="expiring-table-spacing tradies-table"
                responsive
                hover
                show-empty
                :fields="companyFields"
                :items="companyFiles"
                :per-page="perPage"
                :current-page="companyCurrentPage"
                :tbody-tr-class="getRowClass"
                @row-clicked="viewFile"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <digger-loader />
                  </div>
                </template>

                <template #cell(file_owner_name)="data">
                  <div class="cell-content-wrapper">
                    <span
                      :class="{
                        'text-muted': isTradieTokenLocked(data.item),
                      }"
                    >
                      {{ fileOwnerName(data.item) }}
                    </span>
                    <div
                      v-if="isTradieTokenLocked(data.item)"
                      class="lock-overlay"
                      :ref="`lock-over-lay-${data.item.id}`"
                    >
                      <img
                        src="@/assets/images/lock.png"
                        alt="Locked"
                        class="lock-icon mr-2"
                      />
                      <span>{{ tradieLockMessage() }}</span>
                    </div>
                  </div>
                </template>

                <template #cell(file_type)="data">
                  <span
                    v-if="canShowTradieField(data.item)"
                    class="file-type-badge"
                    :class="companyFileTypeBadgeClass(data.item)"
                  >
                    {{ companyFileTypeLabel(data.item) }}
                  </span>
                </template>

                <template #cell(file_name)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.file_name
                  }}</span>
                </template>

                <template #cell(issued_date)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.issued_date
                  }}</span>
                </template>

                <template #cell(expiry_date)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.expiry_date
                  }}</span>
                </template>

                <template #cell(days_left)="data">
                  <span v-if="canShowTradieField(data.item)">{{
                    data.item.days_left
                  }}</span>
                </template>

                <template #cell(last_renewal_requested_at)="data">
                  <span v-if="canShowTradieField(data.item)">
                    <template v-if="data.item.last_renewal_requested_at">
                      {{ data.item.last_renewal_requested_at }}
                      <div
                        v-if="data.item.last_renewal_requested_by"
                        class="last-requested-by"
                      >
                        by {{ data.item.last_renewal_requested_by }}
                      </div>
                    </template>
                    <span v-else>-</span>
                  </span>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    pill
                    variant="primary"
                    class="renew-btn d-inline-flex align-items-center justify-content-center"
                    v-if="canRequestRenewal(data.item)"
                    :disabled="isRequestingRenewal(data.item.id)"
                    :title="renewalButtonTitle(data.item)"
                    @click.stop="requestRenewal(data.item)"
                  >
                    <template v-if="isRequestingRenewal(data.item.id)">
                      <b-spinner small label="Loading" />
                      <span class="d-none d-md-inline ml-2">Sending...</span>
                    </template>

                    <template v-else>
                      <b-icon icon="arrow-repeat" class="d-md-none" />
                      <span class="d-none d-md-inline">Request Renewal</span>
                    </template>
                  </b-button>
                </template>
              </b-table>
            </div>

            <b-pagination
              v-if="companyFiles.length > perPage"
              v-model="companyCurrentPage"
              :total-rows="companyFiles.length"
              :per-page="perPage"
              :limit="5"
              aria-controls="company-files-table"
              align="center"
              class="tradies-pagination"
            />

            <p v-if="!isLoading" align="center" class="tradie-count">
              Showing {{ companyShowingCount }} out of total of
              {{ companyFiles.length }} Expiring Files
            </p>
          </b-container>
        </b-card>

        <!-- Modal - View File -->
        <ViewFileModal
          :showModal="isViewingFile"
          :fileId="viewFileId"
          :readOnly="readOnly"
          :canArchive="isProjectScoped"
          :fromContext="fromContext"
          @close="onViewFileClose"
          @error="onViewFileError"
          @deleted="onViewFileDeleted"
          @marked-na="onViewFileMarkedNa"
        />

        <purchase-token-modal
          v-if="canPurchaseTokens"
          :showModal="isPurchasingToken"
          @closeModal="purchasingToken(false)"
          @purchase="onPurchaseTokens"
        />

        <assign-token-modal
          v-if="isAssigningToken && canAssignTokens"
          :showModal="isAssigningToken"
          :tradieToAssign="tradieToAssign"
          @closeModal="onAssignModalClose"
          @assign="onAssignToken"
          @purchaseTokens="onAssignModalPurchaseTokens"
        />

        <b-modal
          v-model="isSelectingTradie"
          title="Select File Owner"
          centered
          hide-footer
          @hidden="onManualSelectClose"
        >
          <p class="text-muted small mb-2">
            We couldn't automatically identify the file owner. Search for the
            person below to assign a token.
          </p>
          <b-form-input
            v-model="tradieSelectSearch"
            placeholder="Search by name..."
            debounce="400"
          />
          <div v-if="tradieSelectLoading" class="text-center mt-3">
            <b-spinner small />
          </div>
          <b-list-group
            v-else-if="tradieSelectResults.length"
            class="mt-2"
            style="max-height: 240px; overflow-y: auto"
          >
            <b-list-group-item
              v-for="t in tradieSelectResults"
              :key="`${t._personType}-${t.id}`"
              :active="
                tradieSelectChosen &&
                tradieSelectChosen.id === t.id &&
                tradieSelectChosen._personType === t._personType
              "
              button
              @click="tradieSelectChosen = t"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span>
                  {{ t.full_name || t.FullName }}
                  <span v-if="t.email" class="text-muted small ml-1">{{
                    t.email
                  }}</span>
                </span>
                <b-badge
                  :variant="t._personType === 'ba' ? 'info' : 'secondary'"
                  class="ml-2 flex-shrink-0"
                >
                  {{ t._personType === 'ba' ? 'Business Admin' : 'Tradie' }}
                </b-badge>
              </div>
            </b-list-group-item>
          </b-list-group>
          <p
            v-else-if="tradieSelectSearch && !tradieSelectLoading"
            class="text-muted mt-2 small"
          >
            No results found.
          </p>
          <div class="d-flex justify-content-end mt-3" style="gap: 8px">
            <b-button variant="secondary" @click="onManualSelectClose">
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :disabled="!tradieSelectChosen"
              @click="onManualTradieConfirm"
            >
              Assign Token
            </b-button>
          </div>
        </b-modal>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapGetters, mapActions } from 'vuex'
import Api from '@/api'
import ViewFileModal from '@/components/modals/ViewFileModal.vue'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal.vue'
import AssignTokenModal from '@/components/modals/AssignTokenModal.vue'

export default {
  components: {
    ViewFileModal,
    PurchaseTokenModal,
    AssignTokenModal,
  },

  props: {
    projectId: {
      type: Number,
      default: 0,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Hides the "Company Files" group entirely, leaving only the Tradies
     * table. Used by the shared-project view, where a sharee is only meant to
     * see the project's tradies' expiring files, not the sharer company's own
     * documents.
     */
    hideCompanyFiles: {
      type: Boolean,
      default: false,
    },
    /**
     * Tags the "View full file" navigation with where this list is being
     * shown, so ViewFilePage's Back button can return here (or as close to it
     * as a route allows) instead of falling through to a generic default.
     * Unrelated to `permissionPrefix`, which drives real permission checks.
     */
    fromContext: {
      type: String,
      default: null,
    },
    /**
     * Overrides the locked-row message. The shared-project view passes a copy
     * pointing the sharee at the sharing company (they can't assign a token
     * themselves), instead of the default "Assign token to view".
     */
    lockMessage: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      perPage: 10,
      // Tradies and Company Files each paginate and search independently
      // now that they're rendered as two separate tables.
      tradieCurrentPage: 1,
      companyCurrentPage: 1,
      tradieSearch: '',
      companySearch: '',

      // Full, unfiltered result set (all pages fetched up front — see
      // fetchAllExpiringFiles), split client-side by is_tradie_file and
      // filtered client-side by each table's own search box below. The
      // "Expiring Soon (30 days)" window keeps this small in practice, so
      // fetching it all at once beats juggling two independently-paginated,
      // independently-searched server queries (the API has no owner-type
      // filter to split on).
      allExpiringFiles: [],

      isLoading: false,
      isViewingFile: false,

      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,

      viewFileId: 0,
      requestingRenewals: {},

      isPurchasingToken: false,
      isAssigningToken: false,
      tradieToAssign: null,

      /** Local cache from GET companies/tradies (does not replace Vuex). */
      lookupTradies: null,
      lookupTradiesPromise: null,

      isSelectingTradie: false,
      tradieSelectSearch: '',
      tradieSelectResults: [],
      tradieSelectLoading: false,
      tradieSelectChosen: null,
      manualAssignFile: null,
    }
  },

  computed: {
    ...mapGetters('companyFile', [
      'expiringTradieFiles',
      'expiringTradieFilesMeta',
    ]),

    ...mapGetters('auth', ['user', 'hasPermission', 'isSupervisorTradie']),

    ...mapGetters('token', ['balance']),

    /**
     * Between md (768px, where the mobile card list hands off to this table)
     * and ~1085px, all 8 columns don't fit the card without the `responsive`
     * wrapper's horizontal scroll kicking in. Rather than scroll, progressively
     * drop the least-essential columns as width shrinks (and add them back as
     * it grows) — see buildFields(). Untouched at 1085px+, where the full
     * column set already fits.
     */
    columnTier() {
      const w = this.windowWidth
      if (w < 900) return 'compact'
      if (w < 1050) return 'cozy'
      if (w < 1085) return 'comfortable'
      return 'full'
    },

    /**
     * Tradies table columns. See buildFields() for the responsive column set,
     * and columnTier() for the width thresholds.
     */
    fields() {
      return this.buildFields(false)
    },

    /**
     * Company files have no per-tradie Projects to show; in its place, a Type
     * column distinguishes the BA's own personal documents from a file
     * privately uploaded for a tradie (owned by the tradie, but not visible
     * to them — see is_private_tradie_file), which otherwise look identical.
     */
    companyFields() {
      return this.buildFields(true)
    },

    ...mapGetters('company', ['tradies']),

    /**
     * True when this list is scoped to a project (project details view), where
     * the owners are project tradies — so the file popup offers Archive. The
     * dashboard "Expiring Soon" list has no projectId and never shows it.
     */
    isProjectScoped() {
      return !!this.projectId
    },

    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },

    canAssignTokens() {
      return (
        (this.user && this.user.is_primary) ||
        this.hasPermission('settings.billing.assign')
      )
    },

    tradieFiles() {
      return this.allExpiringFiles.filter(
        (f) => f.is_tradie_file && this.matchesSearch(f, this.tradieSearch),
      )
    },

    companyFiles() {
      return this.allExpiringFiles.filter(
        (f) => !f.is_tradie_file && this.matchesSearch(f, this.companySearch),
      )
    },

    tradieMobilePage() {
      const start = (this.tradieCurrentPage - 1) * this.perPage
      return this.tradieFiles.slice(start, start + this.perPage)
    },

    companyMobilePage() {
      const start = (this.companyCurrentPage - 1) * this.perPage
      return this.companyFiles.slice(start, start + this.perPage)
    },

    tradieShowingCount() {
      return this.showingCountFor(
        this.tradieFiles.length,
        this.tradieCurrentPage,
      )
    },

    companyShowingCount() {
      return this.showingCountFor(
        this.companyFiles.length,
        this.companyCurrentPage,
      )
    },
  },

  watch: {
    tradieSearch() {
      this.tradieCurrentPage = 1
    },

    companySearch() {
      this.companyCurrentPage = 1
    },

    tradieSelectSearch(val) {
      if (this.isSelectingTradie) {
        this.doTradieSearch(val)
      }
    },
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },

    /**
     * Builds one table's column set for the current columnTier. On phones the
     * narrow columns can't fit the full header labels on one line, so short
     * labels ("Owner", "Days") are used below 576px — moot in practice since
     * the mobile card list (not this table) covers that width, but kept for
     * consistency. Explicit `thStyle` widths (paired with `table-layout:
     * fixed` in the scoped styles below) always sum to 100% per tier so
     * neither table is ever left narrower than its card.
     */
    buildFields(isCompany) {
      const short = this.windowWidth < 576
      const tier = this.columnTier

      const ownerCol = {
        key: 'file_owner_name',
        label: short ? 'Owner' : 'File Owner',
        sortable: true,
        thClass: 'text-nowrap',
      }
      const projectsCol = { key: 'project_names', label: 'Projects' }
      const typeCol = {
        key: 'file_type',
        label: 'Type',
        thClass: 'text-nowrap',
      }
      const fileCol = {
        key: 'file_name',
        label: 'File',
        sortable: true,
        thClass: 'text-nowrap',
      }
      const issuedCol = {
        key: 'issued_date',
        label: 'Date of Issue',
        sortable: true,
      }
      const expiryCol = {
        key: 'expiry_date',
        label: 'Expiry Date',
        sortable: true,
        thClass: 'text-nowrap',
      }
      const daysCol = {
        key: 'days_left',
        label: short ? 'Days' : 'Days Left',
        sortable: true,
        thClass: 'text-nowrap text-center',
        tdClass: 'text-center',
      }
      const lastReqCol = {
        key: 'last_renewal_requested_at',
        label: short ? 'Last Req.' : 'Last Requested',
        thClass: 'text-nowrap',
      }
      const actionsCol = {
        key: 'actions',
        label: 'Action',
        thClass: 'text-right',
        tdClass: 'text-right',
      }

      let columns
      let widths

      if (tier === 'compact') {
        columns = [ownerCol, fileCol, expiryCol, daysCol, actionsCol]
        widths = {
          file_owner_name: '26%',
          file_name: '30%',
          expiry_date: '20%',
          days_left: '10%',
          actions: '14%',
        }
      } else if (tier === 'cozy') {
        columns = [
          ownerCol,
          fileCol,
          expiryCol,
          daysCol,
          lastReqCol,
          actionsCol,
        ]
        widths = {
          file_owner_name: '22%',
          file_name: '26%',
          expiry_date: '16%',
          days_left: '8%',
          last_renewal_requested_at: '16%',
          actions: '12%',
        }
      } else if (tier === 'comfortable') {
        columns = [
          ownerCol,
          fileCol,
          issuedCol,
          expiryCol,
          daysCol,
          lastReqCol,
          actionsCol,
        ]
        widths = {
          file_owner_name: '18%',
          file_name: '22%',
          issued_date: '14%',
          expiry_date: '14%',
          days_left: '8%',
          last_renewal_requested_at: '14%',
          actions: '10%',
        }
      } else if (isCompany) {
        // 'full' tier, Company Files — unchanged from the original layout.
        columns = [
          ownerCol,
          typeCol,
          fileCol,
          issuedCol,
          expiryCol,
          daysCol,
          lastReqCol,
          actionsCol,
        ]
        widths = {
          file_owner_name: '14%',
          file_type: '12%',
          file_name: '16%',
          issued_date: '12%',
          expiry_date: '12%',
          days_left: '8%',
          last_renewal_requested_at: '14%',
          actions: '12%',
        }
      } else {
        // 'full' tier, Tradies — unchanged from the original layout.
        columns = [
          ownerCol,
          projectsCol,
          fileCol,
          issuedCol,
          expiryCol,
          daysCol,
          lastReqCol,
          actionsCol,
        ]
        widths = {
          file_owner_name: '14%',
          project_names: '12%',
          file_name: '16%',
          issued_date: '12%',
          expiry_date: '12%',
          days_left: '8%',
          last_renewal_requested_at: '14%',
          actions: '12%',
        }
      }

      return columns.map((col) => ({
        ...col,
        thStyle: { width: widths[col.key] },
      }))
    },

    ...mapActions('companyFile', {
      getExpiringTradieFiles: 'getExpiringTradieFiles',
      requestFileRenewal: 'requestFileRenewal',
    }),
    ...mapActions('token', {
      purchase: 'purchase',
      assign: 'assign',
    }),

    /**
     * Kept as a thin alias so external callers (ViewPage.vue refreshes this
     * list by ref after assigning a token) don't need to know the Tradies /
     * Company Files split fetches everything at once now rather than one
     * server-paginated page.
     */
    async fetchExpiringTradieFiles() {
      await this.fetchAllExpiringFiles()
    },

    /**
     * Pulls every expiring file (no search param — Tradies and Company Files
     * now each filter their own copy client-side, see matchesSearch), looping
     * pages if the company has more than fit in one bulk request. The 30-day
     * "Expiring Soon" window keeps this small in practice — this trades a
     * few extra requests (rare) for correct client-side pagination/totals on
     * both tables, without needing the API to support filtering by owner
     * type.
     */
    async fetchAllExpiringFiles() {
      this.isLoading = true

      const bulkLimit = 500
      let page = 1
      let all = []

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const payload = {
            limit: bulkLimit,
            page,
            projectId: this.projectId,
          }

          await this.getExpiringTradieFiles(payload)
          const items = this.expiringTradieFiles || []
          all = all.concat(items)

          const meta = this.expiringTradieFilesMeta
          const lastPage = meta ? meta.last_page : page
          if (!items.length || page >= lastPage) break
          page += 1
        }
        this.allExpiringFiles = all
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    matchesSearch(file, term) {
      const q = (term || '').trim().toLowerCase()
      if (!q) return true
      const owner = (file.file_owner_name || '').toLowerCase()
      const projects = (file.project_names || '').toLowerCase()
      const name = (file.file_name || '').toLowerCase()
      return owner.includes(q) || projects.includes(q) || name.includes(q)
    },

    showingCountFor(total, currentPage) {
      if (!total) return 0
      const start = (currentPage - 1) * this.perPage
      return Math.max(0, Math.min(this.perPage, total - start))
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    async viewFile(item, i) {
      if (this.isSupervisorTradie) {
        if (!item.can_access) return
      } else if (!item.can_access) {
        if (this.canAssignTokens) {
          await this.openAssignTokenForLockedFile(item)
        }
        return
      }
      this.viewFileId = item.id
      this.isViewingFile = true
    },

    tradieIdFromExpiringFile(file) {
      if (!file || typeof file !== 'object') return null
      const owner = file.file_owner
      const user = file.user
      const candidates = [
        file.tradie_id,
        file.tradieId,
        file.TradieId,
        file.file_owner_id,
        file.fileOwnerId,
        file.FileOwnerId,
        file.file_owner_tradie_id,
        file.fileOwnerTradieId,
        file.FileOwnerTradieId,
        file.user_id,
        file.userId,
        file.UserId,
        file.owner_id,
        file.ownerId,
        file.OwnerId,
        file.file_user_id,
        file.fileUserId,
        file.FileUserId,
        file.actor_id,
        file.actorId,
        file.ActorId,
        file.uploaded_by_id,
        file.uploadedById,
        file.UploadedById,
        file.holder_id,
        file.holderId,
        file.HolderId,
        owner &&
          (owner.tradie_id ||
            owner.tradieId ||
            owner.TradieId ||
            owner.id ||
            owner.Id ||
            owner.user_id ||
            owner.UserId),
        user &&
          (user.tradie_id ||
            user.tradieId ||
            user.TradieId ||
            user.id ||
            user.Id),
      ]
      for (const c of candidates) {
        if (c === undefined || c === null || c === '') continue
        const n = typeof c === 'string' ? parseInt(c, 10) : c
        if (Number.isFinite(n) && n > 0) return n
      }
      return null
    },

    normalizePersonName(value) {
      if (value == null || typeof value !== 'string') return ''
      return value.replace(/\s+/g, ' ').trim().toLowerCase()
    },

    matchTradieRosterRow(file, rows) {
      const raw = (file && file.file_owner_name) || ''
      const name = raw.trim()
      if (!name || !Array.isArray(rows) || !rows.length) return null

      const target = this.normalizePersonName(name)
      if (!target) return null

      const rowName = (row) =>
        this.normalizePersonName(row.full_name || row.FullName || '')

      let hit = rows.find((row) => {
        const fn = rowName(row)
        return fn && fn === target
      })
      if (!hit && rows.length === 1) {
        hit = rows[0]
      }
      if (!hit) {
        hit = rows.find((row) => {
          const fn = rowName(row)
          return fn && (fn.includes(target) || target.includes(fn))
        })
      }
      if (!hit) return null

      const rawId = hit.id != null ? hit.id : hit.Id
      const n = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId
      if (!Number.isFinite(n) || n <= 0) return null

      return { id: n, row: hit }
    },

    tryResolveTradieFromLocalRosters(file) {
      const storeRows = Array.isArray(this.tradies) ? this.tradies : []
      if (storeRows.length) {
        const fromStore = this.matchTradieRosterRow(file, storeRows)
        if (fromStore) return fromStore
      }
      const cached = Array.isArray(this.lookupTradies) ? this.lookupTradies : []
      if (cached.length) {
        return this.matchTradieRosterRow(file, cached)
      }
      return null
    },

    /**
     * Prefetch company tradies once so name→id resolution does not block the first click.
     */
    ensureLookupTradiesLoaded() {
      if (this.lookupTradiesPromise) {
        return this.lookupTradiesPromise
      }
      this.lookupTradiesPromise = (async () => {
        try {
          const qs = new URLSearchParams({
            limit: '500',
            page: '1',
            search: '',
            archived_only: 'false',
          })
          const { data } = await Api.get(`companies/tradies?${qs.toString()}`)
          this.lookupTradies = Array.isArray(data && data.data) ? data.data : []
        } catch (e) {
          this.lookupTradies = []
        }
        return this.lookupTradies
      })()
      return this.lookupTradiesPromise
    },

    /**
     * Fallback: narrow search when the bulk prefetch did not include a match (large rosters).
     */
    async resolveTradieFromCompanySearch(file) {
      const raw = (file && file.file_owner_name) || ''
      const name = raw.trim()
      if (!name) return null

      try {
        const qs = new URLSearchParams({
          limit: '50',
          page: '1',
          search: name,
          archived_only: 'false',
        })
        const { data } = await Api.get(`companies/tradies?${qs.toString()}`)
        const rows = Array.isArray(data && data.data) ? data.data : []
        return this.matchTradieRosterRow(file, rows)
      } catch (e) {
        return null
      }
    },

    async resolveTradieForLockedFileRow(file) {
      const local = this.tryResolveTradieFromLocalRosters(file)
      if (local) return local
      await this.ensureLookupTradiesLoaded()
      const afterPrefetch = this.matchTradieRosterRow(
        file,
        this.lookupTradies || [],
      )
      if (afterPrefetch) return afterPrefetch
      return this.resolveTradieFromCompanySearch(file)
    },

    async openAssignTokenForLockedFile(file) {
      const balanceReady =
        this.balance != null && typeof this.balance.available === 'number'
      const balanceRefresh = this.$store
        .dispatch('token/getBalance')
        .catch(() => {})
      if (!balanceReady) {
        await balanceRefresh
      }

      const available =
        this.balance && typeof this.balance.available === 'number'
          ? this.balance.available
          : 0

      let tid = this.tradieIdFromExpiringFile(file)
      let rosterRow = null
      if (tid == null) {
        const resolved = await this.resolveTradieForLockedFileRow(file)
        if (resolved) {
          tid = resolved.id
          rosterRow = resolved.row
        }
      }

      const openAssignModal = () => {
        const emailFromRoster =
          rosterRow &&
          (rosterRow.email || rosterRow.Email || rosterRow.user_email)
        let accepted = 1
        if (
          rosterRow &&
          rosterRow.accepted !== undefined &&
          rosterRow.accepted !== null
        ) {
          accepted = rosterRow.accepted
        } else if (file.accepted != null) {
          accepted = file.accepted
        }
        this.tradieToAssign = {
          id: tid,
          full_name: file.file_owner_name,
          email: emailFromRoster || file.file_owner_email || file.email || '',
          accepted,
        }
        this.isAssigningToken = true
      }

      if (available > 0) {
        if (tid == null) {
          await this.openManualTradieSelect(file)
          return
        }
        openAssignModal()
        return
      }

      if (tid == null) {
        if (this.canPurchaseTokens) {
          this.purchasingToken(true)
        }
        return
      }

      openAssignModal()
    },

    async onAssignToken() {
      if (!this.tradieToAssign) return
      const companyId = this.user && this.user.company_id
      if (!companyId) {
        this.showToast('Error', 'Company ID not found', 'danger')
        return
      }
      this.isLoading = true
      try {
        await this.assign(this.tradieToAssign.id, companyId)
        this.showToast('Success', 'Token assigned successfully')
        this.isAssigningToken = false
        this.tradieToAssign = null
        await this.$store.dispatch('token/getBalance')
        await this.fetchAllExpiringFiles()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    onAssignModalClose() {
      this.isAssigningToken = false
      this.tradieToAssign = null
    },

    onAssignModalPurchaseTokens() {
      this.isAssigningToken = false
      if (this.canPurchaseTokens) {
        this.purchasingToken(true)
      }
    },

    async openManualTradieSelect(file) {
      this.manualAssignFile = file
      this.tradieSelectSearch = (file && file.file_owner_name) || ''
      this.tradieSelectChosen = null
      this.tradieSelectResults = []
      this.isSelectingTradie = true
      if (this.tradieSelectSearch) {
        await this.doTradieSearch(this.tradieSelectSearch)
      }
    },

    async doTradieSearch(term) {
      this.tradieSelectLoading = true
      try {
        const qs = new URLSearchParams({
          limit: '50',
          page: '1',
          search: term || '',
          archived_only: 'false',
        })
        const [tradieRes, baRes] = await Promise.allSettled([
          Api.get(`companies/tradies?${qs.toString()}`),
          Api.get(`company/business-admins`, {
            params: { search: term || '', archived_only: false },
          }),
        ])
        const tradies =
          tradieRes.status === 'fulfilled' &&
          Array.isArray(tradieRes.value.data && tradieRes.value.data.data)
            ? tradieRes.value.data.data.map((t) => ({
                ...t,
                _personType: 'tradie',
              }))
            : []
        const baRaw = baRes.status === 'fulfilled' ? baRes.value.data : null
        const baList = Array.isArray(baRaw && baRaw.data)
          ? baRaw.data
          : Array.isArray(baRaw)
          ? baRaw
          : []
        const bas = baList.map((b) => {
          const user = b.user || b
          return {
            ...b,
            id: b.id || b.user_id || (user && user.id),
            full_name: b.full_name || (user && user.full_name),
            email: b.email || (user && user.email),
            _personType: 'ba',
          }
        })
        this.tradieSelectResults = [...tradies, ...bas]
      } catch (e) {
        this.tradieSelectResults = []
      } finally {
        this.tradieSelectLoading = false
      }
    },

    async onManualTradieConfirm() {
      if (!this.tradieSelectChosen) return
      const file = this.manualAssignFile
      const t = this.tradieSelectChosen
      const rawId = t.id != null ? t.id : t.Id
      const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId
      if (!Number.isFinite(id) || id <= 0) return

      this.isSelectingTradie = false

      if (t._personType === 'ba') {
        await this.assignTokenToBaById(id)
        return
      }

      const email =
        t.email ||
        t.Email ||
        t.user_email ||
        (file && (file.file_owner_email || file.email)) ||
        ''
      const accepted =
        t.accepted != null
          ? t.accepted
          : file && file.accepted != null
          ? file.accepted
          : 1
      this.tradieToAssign = {
        id,
        full_name:
          t.full_name || t.FullName || (file && file.file_owner_name) || '',
        email,
        accepted,
      }
      this.isAssigningToken = true
    },

    async assignTokenToBaById(userId) {
      this.isLoading = true
      try {
        await Api.post('company/assign-token-to-ba', { user_id: userId })
        this.showToast('Success', 'Token assigned successfully')
        await this.$store.dispatch('token/getBalance')
        await this.fetchAllExpiringFiles()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    onManualSelectClose() {
      this.isSelectingTradie = false
      this.manualAssignFile = null
      this.tradieSelectSearch = ''
      this.tradieSelectResults = []
      this.tradieSelectChosen = null
    },

    purchasingToken(status) {
      this.isPurchasingToken = status
    },

    async onPurchaseTokens(quantity) {
      if (!quantity || quantity < 1) return
      await this.purchase(quantity).catch((err) => this.handleError(err))
      this.purchasingToken(false)
      await this.$store.dispatch('token/getBalance').catch(() => {})
      if (this.tradieToAssign) {
        this.isAssigningToken = true
      }
    },

    getRowClass(item) {
      if (!item) return 'cursor-pointer'
      const baseClass = 'cursor-pointer'
      if (this.isTradieTokenLocked(item)) {
        return `${baseClass} row-locked`
      }
      return baseClass
    },

    isTradieTokenLocked(item) {
      return !!(item && !item.can_access)
    },

    canShowTradieField(item) {
      if (!item) return false
      return !!item.can_access
    },

    tradieLockMessage() {
      if (this.lockMessage) return this.lockMessage
      if (this.isSupervisorTradie) {
        return 'Locked - Contact your business admin to unlock'
      }
      return 'Locked - Assign token to view'
    },

    onViewFileClose() {
      this.isViewingFile = false
    },

    onViewFileError(err) {
      this.handleError(err)
    },

    onViewFileDeleted(fileId) {
      const i = this.allExpiringFiles.findIndex((x) => x.id === fileId)
      if (i > -1) this.allExpiringFiles.splice(i, 1)
      this.onViewFileClose()
    },

    handleError(err) {
      const message =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'An unexpected error occurred.'

      this.showToast('Oops.. Something went wrong!', message, 'danger')
    },

    isUsers(file) {
      if (!file || !this.user) return false
      return file.file_owner_name === this.user.full_name
    },

    isRequestingRenewal(fileId) {
      return !!this.requestingRenewals[fileId]
    },

    onViewFileMarkedNa(file) {
      this.fetchAllExpiringFiles()
    },

    async requestRenewal(file) {
      const confirmation = await this.$bvModal.msgBoxConfirm(
        `Send a renewal request to ${file.file_owner_name}?`,
        {
          title: 'Request renewal',
          size: 'md',
          centered: true,
          okVariant: 'primary',
          okTitle: 'Send request',
          cancelTitle: 'Cancel',
          footerClass: 'p-2 renewal-confirm-footer',
          modalClass: 'renewal-confirm-modal',
          hideHeaderClose: false,
        },
      )

      if (!confirmation) {
        return
      }

      this.$set(this.requestingRenewals, file.id, true)

      try {
        await this.requestFileRenewal({ fileId: file.id })
        this.showToast(
          'Renewal requested',
          `${file.file_owner_name} will be notified.`,
        )
        this.markRenewalRequestedLocally(file.id)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.$delete(this.requestingRenewals, file.id)
      }
    },

    /**
     * Reflects a just-sent renewal request in the "Last Requested" column
     * immediately, without waiting on a full fetchAllExpiringFiles() round
     * trip. Mirrors the 'd/m/Y' format the API sends for
     * last_renewal_requested_at (CompanyFilesController::index) so this
     * matches server-refreshed rows once the page is next reloaded.
     */
    markRenewalRequestedLocally(fileId) {
      const item = this.allExpiringFiles.find((f) => f.id === fileId)
      if (!item) return
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const formatted = `${pad(now.getDate())}/${pad(
        now.getMonth() + 1,
      )}/${now.getFullYear()}`
      this.$set(item, 'last_renewal_requested_at', formatted)
      this.$set(
        item,
        'last_renewal_requested_by',
        this.user ? this.user.full_name : null,
      )
    },

    fileOwnerName(file) {
      if (!file) {
        return ''
      }
      return file.file_owner_name
    },

    isTradieFile(file) {
      if (!file) {
        return false
      }
      return file.is_tradie_file
    },

    /**
     * Gates the Request Renewal action everywhere it appears (both tables,
     * both mobile card lists). Every Company Files row has is_tradie_file
     * false by definition, so without the extra clauses below only the
     * (rare) shared-in rows would ever qualify — leaving every plain
     * "Company File" row (a colleague's own file, same company) with no
     * button at all, even though the API has always allowed a same-company
     * BA to request that colleague renew it.
     *
     * Qualifies when:
     *  - it's a tradie's own file (isTradieFile), or
     *  - it was peer-to-peer shared to the viewer by another company's BA
     *    (is_shared_to_viewer) — the request still goes to the actual owner
     *    (the sharer), this just lets the recipient — who's the one needing
     *    the file kept current — ask, or
     *  - it's a same-company colleague's own file (is_tradie_file false,
     *    and NOT is_private_tradie_file — that one's still excluded on
     *    purpose, since its owner-of-record is a tradie who can't see it;
     *    requesting "renewal" there would notify someone who can't act on
     *    it).
     */
    canRequestRenewal(file) {
      const isCompanysOwnFile =
        !file.is_tradie_file && !file.is_private_tradie_file
      return (
        !this.readOnly &&
        !this.isUsers(file) &&
        this.canShowTradieField(file) &&
        (this.isTradieFile(file) ||
          !!file.is_shared_to_viewer ||
          isCompanysOwnFile)
      )
    },

    /**
     * Warns against sending a duplicate ask: if this file already had a
     * renewal requested, the button's hover text says when and by whom,
     * instead of the requester having to just guess whether one's already
     * pending.
     */
    renewalButtonTitle(file) {
      if (!file.last_renewal_requested_at) return 'Request renewal'
      const by = file.last_renewal_requested_by
        ? ` by ${file.last_renewal_requested_by}`
        : ''
      return `Request renewal - last requested ${file.last_renewal_requested_at}${by}`
    },

    /**
     * Company Files' Type column/label: distinguishes the BA's own personal
     * documents, a BA-private file made for a tradie, and a file peer-shared
     * in by another company's BA — three otherwise identical-looking rows.
     */
    companyFileTypeLabel(file) {
      if (file.is_shared_to_viewer) {
        return file.shared_by_company_name
          ? `Shared (${file.shared_by_company_name})`
          : 'Shared'
      }
      return file.is_private_tradie_file ? 'Private (Tradie)' : 'Company File'
    },

    companyFileTypeBadgeClass(file) {
      if (file.is_shared_to_viewer) return 'file-type-badge--shared'
      return file.is_private_tradie_file
        ? 'file-type-badge--private'
        : 'file-type-badge--mine'
    },
  },

  async mounted() {
    window.addEventListener('resize', this.onResize)
    await this.fetchAllExpiringFiles()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer {
  cursor: pointer;
}

/* Matches ProjectList's table (the mt-4 utility) so the search-to-table gap
   reads the same across both dashboard sections. */
/* Matches ProjectList's table (the mt-4 utility) so the search-to-table gap
   reads the same across both dashboard sections. */
.expiring-table-spacing {
  margin-top: 1.5rem;
}

/* Both tables share the `fields` column definitions (with explicit `thStyle`
   widths — see the fields() computed), but bootstrap's default auto layout
   still sizes each table's columns off its own content, which can drift the
   two tables out of alignment when stacked (e.g. the Action column is empty
   on every Company Files row, so its "renew" button never forces that
   column wide there the way it does on Tradies). Fixed layout makes both
   tables honour the shared widths instead, so columns line up. */
/deep/ .expiring-table-spacing {
  table-layout: fixed;
}

/* Tradies and Company Files each get their own plain card (rather than
   sharing the page's outer "Expiring Soon"/"Expiring Files" card) so the two
   file types read as clearly separate groups, not one blended list. */
.expiring-group-card {
  overflow: hidden;
}

.expiring-group-card /deep/ .card-header {
  font-size: 1rem;
}

/* Explicit, consistent gap above the pagination — matches the other three
   tables on the Project Details page (Tradies/Needing Attention/Expired
   Requests, via .tradies-pagination in ViewPage.vue) rather than depending
   on each list's own incidental bottom padding. */
.tradies-pagination {
  margin-top: 0.5rem;
}

/* .expiring-search-col keeps Bootstrap's `col-12` class at every breakpoint
   (cols="12" plus sm/md/lg on top of it), so the global `.col-12 { margin-bottom:
   30px }` override (see global-css-gotchas) was still landing on desktop —
   stacking with .expiring-table-spacing's margin-top above and nearly
   tripling the intended gap. The mobile media query below already zeroes
   this out for phones; do the same unconditionally so desktop isn't left out. */
.expiring-search-col {
  margin-bottom: 0;
  padding-left: 0.9375rem;
  padding-right: 0.9375rem;
}

/* This component's own ROOT <b-container fluid> carries Bootstrap's default
   0.9375rem gutter on top of whatever the parent row in ViewPage.vue already
   contributes — the other two phone sections (Expired Requests, Needing
   Attention) render directly with no such wrapper, so this extra layer alone
   was making Expiring Files' rows noticeably narrower/more inset than theirs.
   Zero it out on phones; keep it at md+, where ViewPage.vue's own layout
   deliberately relies on this container's natural padding to match Expiring
   Files against those two sections (see the "Expiring Files' content..."
   comment there).
   Scoped to .expiring-root-container specifically (not the bare
   .container-fluid class) — that selector used to also catch each card's own
   INNER <b-container fluid> (the one wrapping the search row). Zeroing an
   inner container's padding leaves its <b-row>'s standard -0.9375rem margin
   uncancelled, which ate the search column's own padding entirely and put
   the search box flush against — sometimes past — the card edge. The inner
   containers were never the extra layer that needed zeroing, so they now
   keep their natural padding at every width, and .expiring-search-col's
   unconditional padding below is enough on its own again. */
@media (max-width: 767.98px) {
  .expiring-root-container {
    padding-left: 0;
    padding-right: 0;
  }
}

/deep/ .cursor-pointer td {
  vertical-align: middle;
}

/* Renewal action button: flex-centred content (fixes the vertically-off label)
   and a compact icon on phones. */
.renew-btn {
  height: 32px;
  padding: 0 0.75rem;
  line-height: 1;
  white-space: nowrap;
}
@media (max-width: 575px) {
  .renew-btn {
    padding: 0 0.5rem;
    font-size: 1rem;
  }
}

/* Company Files' Type column: distinguishes the BA's own personal documents,
   a file a BA privately uploaded for a tradie (owned by the tradie, but not
   visible to them), and a file peer-shared in by another company's BA —
   otherwise identical-looking rows in this table. */
.file-type-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.file-type-badge--mine {
  background: #e9eef5;
  color: #364052;
}

.file-type-badge--private {
  background: #fdf0e0;
  color: #8a5a00;
}

.file-type-badge--shared {
  background: #e0f0fd;
  color: #0a5d8a;
}

/* Last Requested column: the date is the primary value, "by <name>" is
   secondary context — kept visually subordinate rather than competing with
   the date for attention. */
.last-requested-by {
  font-size: 0.75rem;
  color: #8b95a3;
  white-space: nowrap;
}

/* Confine the "Locked - Assign token to view" overlay to the row width so it
   wraps inside the row instead of spilling off the page (the shared _table.scss
   makes it a fixed-width nowrap banner). Higher specificity (tr) so this wins. */
/deep/ .tradies-table tr.row-locked .lock-overlay {
  left: 0;
  right: 0;
  width: auto;
  max-width: 100%;
  transform: translateY(-50%);
  white-space: normal;
  justify-content: center;
  text-align: center;
  padding: 0 6px;
}

/* Narrow screens can't fit the message — show just the lock icon. */
@media (max-width: 575.98px) {
  /deep/ .tradies-table tr.row-locked .lock-overlay span {
    display: none;
  }
  /deep/ .tradies-table tr.row-locked .lock-overlay .lock-icon {
    margin-right: 0 !important;
  }
}

@media (max-width: 575px) {
  /* This list lives in a narrow card column, so instead of scrolling it
     sideways we compress it to FIT: columns size to their content (auto
     layout) and long values (owner / file name) wrap onto multiple lines
     rather than forcing the table wider than the card. `!important` beats
     the parent page's `.tradies-table` nowrap rule that leaks in here. */
  /deep/ .table {
    font-size: 0.72rem;
    width: 100%;

    th,
    td {
      padding: 0.3rem 0.35rem;
      white-space: normal !important;
      word-break: break-word;
      vertical-align: top;
    }

    /* Keep the short numeric column tight so text columns get the room. */
    td.text-center,
    th.text-center {
      white-space: nowrap !important;
    }

    .btn-sm {
      font-size: 0.65rem;
      padding: 0.15rem 0.35rem;
    }
  }

  /* Sizing now lives on .tradies-pagination (below, shared with
     ViewPage.vue's own copy of this rule) so all four tables' pagination
     is guaranteed to read the exact same size on phones, regardless of any
     other rule that might otherwise apply. */
  .tradies-pagination {
    font-size: 0.85rem !important;
  }
  .tradies-pagination /deep/ .page-link {
    padding: 0.25rem 0.5rem !important;
  }

  /* Shrink the "Showing X out of total of Y" summary line — matches
     ViewPage.vue's own .tradie-count rule for the other three tables (this
     one had no size override at all before, reading noticeably larger). */
  .tradie-count {
    font-size: 0.8rem;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
  }

  /* Compact search bar: smaller text so the full placeholder ("Owner,
     project or file name") fits the narrow field, but keep the same 50px
     box height as ProjectList's search input (global .form-control default)
     so the two match. Tighten mt-3's 1rem top gap on phones (margin-bottom:0
     is handled unconditionally above). The column keeps the container's
     default gutter now — same inset as the file rows below it and as
     ProjectList's search box. */
  .expiring-search-col {
    margin-top: 0.5rem !important;
  }
  .expiring-search-input {
    font-size: 0.82rem;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
  }

  /* The search row and table had a large combined gap (row's own bottom
     spacing + this table's top margin) — tighten it on phones. */
  .expiring-table-spacing {
    margin-top: 0 !important;
  }
}

/* ------------------------------------------------------------------ *
 * Mobile expiring-file rows (< md): stacked "card" rows matching the
 * tradie lists — bold owner name, a small meta line (file · expiry ·
 * days left), an inline renew action, and a divider. Hidden on desktop
 * via `d-md-none`, so these styles never touch the >= md table.
 * ------------------------------------------------------------------ */
.mobile-file-list {
  margin-top: 0.5rem;
}

/* Empty state ("No expiring files.") shouldn't leave a big gap on phones. */
.mobile-file-list .py-4 {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}

.mobile-file-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.65rem 0.6rem 0.65rem 0.85rem;
  border-bottom: 1px solid #e1e6ea;
  background: #fff;
}

.mobile-file-item.row-locked {
  background: #f5f5f5;
}

.mobile-file__body {
  flex: 1 1 auto;
  min-width: 0;
}

.mobile-file__name {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-file__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The locked message can't be meaningfully truncated — let it wrap instead
   of clipping mid-sentence. */
.mobile-file__meta--lock {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.mobile-file__lock-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.mobile-file__action {
  flex-shrink: 0;
}

.mobile-file__renew-btn {
  height: 32px;
  width: 32px;
  padding: 0;
}
</style>

<!-- Non-scoped: the "Request renewal" confirm dialog renders at the body level,
     so its footer must be styled globally. Keep the buttons on one line as a
     consistent pair (equal full-width halves on phones). -->
<style>
.renewal-confirm-footer {
  flex-wrap: nowrap;
  gap: 0.5rem;
  /* Right-aligned, matching the other confirm modals across the app.
     Bootstrap's own .modal-footer rule already defaults to flex-end (no
     !important), so this is redundant most of the time — but !important
     guarantees it explicitly rather than depending on that default. */
  justify-content: flex-end !important;
}
.renewal-confirm-footer > .btn {
  min-width: 110px;
  font-weight: 600;
}
@media (max-width: 575.98px) {
  .renewal-confirm-footer {
    /* Centred on phones specifically — the buttons don't reliably fill the
       full row width at every phone size, so right-aligning (the desktop
       default above) can leave them looking off-centre/flush left instead
       of visually balanced. */
    justify-content: center !important;
    /* A bit more breathing room below the buttons instead of them sitting
       flush against the modal's bottom edge. */
    margin-bottom: 0.5rem;
  }
  .renewal-confirm-footer > .btn {
    /* An explicit 50%-minus-half-gap basis (flex-grow: 0) guarantees Cancel
       and Send request end up exactly the same width — a grow-based
       "flex: 1 1 0" leaves that to each button's content/min-content sizing
       resolving identically, which "Send request" (much longer than
       "Cancel") doesn't reliably do. */
    flex: 0 0 calc(50% - 0.25rem);
    min-width: 0;
    /* "Send request" doesn't fit on one line at 320px and was wrapping
       inside a fixed 44px-tall button, clipping the second line. Let the
       button grow to fit wrapped text instead — min-height (not a fixed
       height) keeps the single-line "Cancel" button the same size since
       flex align-items: stretch (the footer's default) matches both
       buttons to the taller one either way. */
    min-height: 44px;
    height: auto;
    padding: 6px 8px;
    font-size: 0.8rem;
    line-height: 1.2;
    white-space: normal;
    text-align: center;
  }

  /* Tighten the surrounding dialog chrome — same treatment as the "Remove
     Tradie from this Project" confirm modal on the Project Details page,
     the default modal padding felt oversized for a two-line prompt. */
  .renewal-confirm-modal .modal-dialog {
    margin: 0.75rem;
  }
  .renewal-confirm-modal .modal-header {
    padding-bottom: 0;
  }
  .renewal-confirm-modal .modal-title {
    font-size: 1.15rem;
  }
  .renewal-confirm-modal .modal-body {
    padding: 0.4rem 0.75rem 0.75rem;
    font-size: 0.9rem;
  }
  .renewal-confirm-modal .modal-footer {
    padding-top: 0.5rem;
  }
}
</style>
